import { Concept } from '../../../models/Entities/Contracts/Concept';
import { ConceptCategory } from '../../../models/Entities/Contracts/ConceptCategory';
import { Contract } from '../../../models/Entities/Contracts/Contract';
import { ContractAddendum } from '../../../models/Entities/Contracts/ContractAddendum';
import { ContractNegotiation } from '../../../models/Entities/Contracts/ContractNegotiation';
import { ContractNegotiationConcept } from '../../../models/Entities/Contracts/ContractNegotiationConcept';
import { ContractNegotiationVersion } from '../../../models/Entities/Contracts/ContractNegotiationVersion';
import { AppState } from '../../../redux/reducers';
import * as ConceptMiddleware from '../../../redux/middleware/ConceptMiddleware';
import * as ConceptCategoryMiddleware from '../../../redux/middleware/ConceptCategoryMiddleware';
import * as ContractNegotiationConceptMiddleware from '../../../redux/middleware/ContractNegotiationConceptMiddleware';
import * as ContractNegotiationVersionMiddleware from '../../../redux/middleware/ContractNegotiationVersionMiddleware';
import * as ContractNegotiationVersionConceptMiddleware from '../../../redux/middleware/ContractNegotiationVersionConceptMiddleware';

export interface OwnProps {
  contractId: number;
  contractNegotiationId: string;
}

interface ConnectedProps {
  negotiationConcepts: ContractNegotiationConcept[];
  versions: ContractNegotiationVersion[];
  allConcepts: Concept[];
  conceptCategories: ConceptCategory[];
  isLoadingConcepts: boolean;
  isLoadingVersions: boolean;
}

interface DispatchProps {
  GetContractNegotiationConcepts: typeof ContractNegotiationConceptMiddleware.actionCreators.GetContractNegotiationConcepts;
  UpdateContractNegotiationConcept: typeof ContractNegotiationConceptMiddleware.actionCreators.UpdateContractNegotiationConcept;
  GetContractNegotiationVersions: typeof ContractNegotiationVersionMiddleware.actionCreators.GetContractNegotiationVersions;
  GetContractNegotiationVersionConcepts: typeof ContractNegotiationVersionConceptMiddleware.actionCreators.GetContractNegotiationVersionConcepts;
  CreateContractNegotiationVersion: typeof ContractNegotiationVersionMiddleware.actionCreators.CreateContractNegotiationVersion;
  CreateContractNegotiationVersionConcept: typeof ContractNegotiationVersionConceptMiddleware.actionCreators.CreateContractNegotiationVersionConcept;
  GetAllConcepts: typeof ConceptMiddleware.actionCreators.GetAllConcepts;
  GetAllConceptCategories: typeof ConceptCategoryMiddleware.actionCreators.GetAllConceptCategories;
}

export type Props = OwnProps & ConnectedProps & DispatchProps;

export const mapStateToProps = (state: AppState, ownProps: OwnProps): ConnectedProps => {
  const contract: Contract | undefined =
    state.Contract?.list?.find((c: Contract) => c.id === ownProps.contractId);

  // Negotiations may be stored in contract.negotiations (top-level) or inside
  // addendum.negotiations (loaded via GetContractNegotiationsByAddendumId).
  // Search both to ensure the correct negotiation is found.
  const negotiation: ContractNegotiation | undefined =
    contract?.negotiations?.find((n) => n.id === ownProps.contractNegotiationId) ||
    contract?.addendums?.flatMap((a: ContractAddendum) => a.negotiations || [])
      .find((n: ContractNegotiation) => n.id === ownProps.contractNegotiationId);

  return {
    negotiationConcepts: negotiation?.concepts || [],
    versions: negotiation?.versions || [],
    allConcepts: state.Concepts?.list || [],
    conceptCategories: state.ConceptCategories?.list || [],
    isLoadingConcepts: negotiation?.isGettingNegotiationConcepts || false,
    isLoadingVersions: negotiation?.isGettingNegotiationVersions || false,
  };
};

export const mapDispatchToProps: DispatchProps = {
  GetContractNegotiationConcepts:
    ContractNegotiationConceptMiddleware.actionCreators.GetContractNegotiationConcepts,
  UpdateContractNegotiationConcept:
    ContractNegotiationConceptMiddleware.actionCreators.UpdateContractNegotiationConcept,
  GetContractNegotiationVersions:
    ContractNegotiationVersionMiddleware.actionCreators.GetContractNegotiationVersions,
  GetContractNegotiationVersionConcepts:
    ContractNegotiationVersionConceptMiddleware.actionCreators.GetContractNegotiationVersionConcepts,
  CreateContractNegotiationVersion:
    ContractNegotiationVersionMiddleware.actionCreators.CreateContractNegotiationVersion,
  CreateContractNegotiationVersionConcept:
    ContractNegotiationVersionConceptMiddleware.actionCreators.CreateContractNegotiationVersionConcept,
  GetAllConcepts: ConceptMiddleware.actionCreators.GetAllConcepts,
  GetAllConceptCategories: ConceptCategoryMiddleware.actionCreators.GetAllConceptCategories,
};

export interface PendingConceptValue {
  conceptId: string;
  sponsorCost: number;
  siteCost: number;
}

export interface VersionConceptInputs {
  [contractNegotiationConceptId: string]: {
    sponsorCost: number;
    siteCost: number;
  };
}

export const formatCurrency = (value: number | undefined): string => {
  if (typeof value !== 'number') return '-';
  return value.toLocaleString('es-AR', { style: 'currency', currency: 'ARS' });
};

export const formatDate = (date: Date | string | undefined | null): string => {
  if (!date) return '-';
  return new Date(date as string).toLocaleDateString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};
